import * as React from 'react'

import { type NonFsdsTheme, resolveNonFsdsTheme } from '@/lib/non-fsds-theme'
import { cn } from '@/lib/utils'

import { customers } from '@/data/customer'

import Marquee from '@/components/common/marquee'

/* ---------------------------------------------------------------------------
 * Logo item used by the GeneralV1 (Marquee) rendering mode.
 * ------------------------------------------------------------------------- */
interface LogoItem {
  title: string
  icon?: React.ReactNode
  image?: string
  alt?: string
  href?: string
  linkTitle?: string
}

/* ---------------------------------------------------------------------------
 * Props accepted by the component.
 *
 * Legacy callers pass `{ title, partners, theme }` → card-based layout.
 * GeneralV1 Puck callers pass `{ heading, logos, bgColor, … }` → Marquee.
 * ------------------------------------------------------------------------- */
interface NonFsdsCorporateCustomerSectionProps {
  /* ---------- legacy (card-based) props ---------- */
  /** @deprecated use `heading` */
  title?: string
  /** @deprecated use `logos` */
  partners?: string[]
  theme?: NonFsdsTheme | string

  /* ---------- GeneralV1 (Marquee) props ---------- */
  className?: string
  heading?: string
  subheading?: string
  headingColor?: string
  subheadingColor?: string
  logos?: LogoItem[]
  logoSource?: 'default' | 'custom'
  logoSize?: string
  bgColor?: string
  textColor?: string
  showHeading?: boolean
  autoPlay?: boolean
  durationSeconds?: number
  pauseOnHover?: boolean
  movementDirection?: 'horizontal' | 'vertical'
  reverse?: boolean
  fade?: boolean
  numberOfCopies?: number
  gapPx?: number
}

/* ---------------------------------------------------------------------------
 * Legacy (card-based) rendering — used by non-FSDS landing pages.
 * ------------------------------------------------------------------------- */
function LegacyCardLayout({
  title,
  partners,
  theme = 'general',
}: {
  title: string
  partners: string[]
  theme?: NonFsdsTheme | string
}) {
  const themeStyles = {
    general: {
      sectionBg: 'from-blue-50/50 to-blue-50/80',
      radialBg: 'bg-[radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.06),transparent_50%)]',
      lineGradient: 'to-blue-300',
      dotBg: 'bg-blue-400',
      cardBorder: 'border-blue-100/80 hover:border-blue-200 hover:shadow-blue-100/40',
      iconWrapper: 'from-blue-100 to-sky-100 text-blue-600 group-hover:from-blue-200 group-hover:to-sky-200',
    },
    'english-academy': {
      sectionBg: 'from-blue-50/50 to-emerald-50/60',
      radialBg: 'bg-[radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.06),transparent_50%)]',
      lineGradient: 'to-blue-300',
      dotBg: 'bg-emerald-400',
      cardBorder: 'border-blue-100/80 hover:border-emerald-200 hover:shadow-emerald-100/40',
      iconWrapper: 'from-blue-100 to-emerald-100 text-blue-600 group-hover:from-blue-200 group-hover:to-emerald-200',
    },
    programming: {
      sectionBg: 'from-indigo-50/50 to-indigo-50/80',
      radialBg: 'bg-[radial-gradient(circle_at_bottom_right,rgba(99,102,241,0.06),transparent_50%)]',
      lineGradient: 'to-indigo-300',
      dotBg: 'bg-indigo-400',
      cardBorder: 'border-indigo-100/80 hover:border-indigo-200 hover:shadow-indigo-100/40',
      iconWrapper: 'from-indigo-100 to-blue-100 text-indigo-600 group-hover:from-indigo-200 group-hover:to-blue-200',
    },
    math: {
      sectionBg: 'from-teal-50/50 to-teal-50/80',
      radialBg: 'bg-[radial-gradient(circle_at_bottom_right,rgba(20,184,166,0.06),transparent_50%)]',
      lineGradient: 'to-teal-300',
      dotBg: 'bg-teal-400',
      cardBorder: 'border-teal-100/80 hover:border-teal-200 hover:shadow-teal-100/40',
      iconWrapper: 'from-teal-100 to-sky-100 text-teal-600 group-hover:from-teal-200 group-hover:to-sky-200',
    },
  }[resolveNonFsdsTheme(theme)]

  return (
    <section
      data-testid="non-fsds-corporate-customer-section"
      className={`relative overflow-hidden bg-gradient-to-b px-4 py-14 sm:px-6 sm:py-16 ${themeStyles.sectionBg}`}
    >
      <div className={`pointer-events-none absolute inset-0 ${themeStyles.radialBg}`} />
      <div className="relative mx-auto max-w-6xl">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">{title}</h2>
          <div className="mx-auto mt-4 flex items-center justify-center gap-2">
            <div className={`h-px w-12 bg-gradient-to-r from-transparent ${themeStyles.lineGradient}`} />
            <div className={`size-1.5 rounded-full ${themeStyles.dotBg}`} />
            <div className={`h-px w-12 bg-gradient-to-l from-transparent ${themeStyles.lineGradient}`} />
          </div>
        </div>

        <ul
          className="mt-8 flex flex-wrap items-center justify-center gap-3 sm:gap-4"
          aria-label="Partner organizations"
        >
          {partners.map(partner => (
            <li key={partner} className="list-none">
              <div className={`group inline-flex min-h-12 cursor-default items-center gap-2.5 rounded-2xl border bg-white/90 px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm backdrop-blur-sm transition-all duration-200 hover:bg-white hover:shadow-md ${themeStyles.cardBorder}`}>
                <span className={`inline-flex size-8 items-center justify-center rounded-lg bg-gradient-to-br transition-colors duration-200 ${themeStyles.iconWrapper}`}>
                  <svg
                    className="size-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3H21m-3.75 3H21"
                    />
                  </svg>
                </span>
                <span>{partner}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}

/* ---------------------------------------------------------------------------
 * GeneralV1 Marquee rendering — used by the Puck page editor.
 * ------------------------------------------------------------------------- */
function MarqueeLayout({
  className,
  heading,
  subheading,
  headingColor = '#1a1a2e',
  subheadingColor = 'rgba(26,26,46,0.7)',
  logos,
  logoSource = 'default',
  logoSize = 'h-8',
  bgColor = '#FAFAFA',
  textColor = '#1a1a2e',
  showHeading = true,
  autoPlay = true,
  durationSeconds = 25,
  pauseOnHover = true,
  movementDirection = 'horizontal',
  reverse = false,
  fade = true,
  numberOfCopies = 5,
  gapPx = 48,
}: Omit<NonFsdsCorporateCustomerSectionProps, 'title' | 'partners' | 'theme'>) {
  const resolvedLogos: LogoItem[] =
    logoSource === 'custom' && logos?.length ? logos : customers as unknown as LogoItem[]

  return (
    <section className={cn('py-10', className)} style={{ backgroundColor: bgColor, color: textColor }}>
      {showHeading && (
        <div className="mx-auto mb-8 max-w-3xl text-center">
          {heading && (
            <h2 className="text-2xl font-bold tracking-tight md:text-3xl" style={{ color: headingColor }}>
              {heading}
            </h2>
          )}
          {subheading && (
            <p className="mt-2 text-base" style={{ color: subheadingColor }}>
              {subheading}
            </p>
          )}
        </div>
      )}

      <Marquee
        autoPlay={autoPlay}
        durationSeconds={durationSeconds}
        pauseOnHover={pauseOnHover}
        direction={movementDirection}
        reverse={reverse}
        fade={fade}
        numberOfCopies={numberOfCopies}
        className="gap-0"
        style={
          fade
            ? ({
                '--gradient-color': bgColor,
              } as React.CSSProperties)
            : undefined
        }
      >
        {resolvedLogos.map((logo, idx) => {
          const content = logo.icon ? (
            <span className={cn(logoSize, 'flex items-center')}>{logo.icon}</span>
          ) : logo.image ? (
            // eslint-disable-next-line
            <img src={logo.image} alt={logo.alt ?? logo.title} className={cn(logoSize, 'object-contain')} />
          ) : (
            <span className="text-sm font-medium">{logo.title}</span>
          )

          return (
            <div key={`${logo.title}-${idx}`} className="flex items-center" style={{ paddingInline: gapPx / 2 }}>
              {logo.href ? (
                <a href={logo.href} title={logo.linkTitle ?? logo.title} target="_blank" rel="noopener noreferrer">
                  {content}
                </a>
              ) : (
                content
              )}
            </div>
          )
        })}
      </Marquee>

      {fade && (
        <>
          <div
            className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24"
            style={{ background: `linear-gradient(to right, ${bgColor}, ${bgColor}cc, transparent)` }}
          />
          <div
            className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24"
            style={{ background: `linear-gradient(to left, ${bgColor}, ${bgColor}cc, transparent)` }}
          />
        </>
      )}
    </section>
  )
}

/* ---------------------------------------------------------------------------
 * Public component — auto-selects rendering mode based on props.
 *
 * • Legacy callers pass `title` + `partners` → LegacyCardLayout
 * • GeneralV1 callers pass `heading` / `logos` / `bgColor` → MarqueeLayout
 * ------------------------------------------------------------------------- */
export default function NonFsdsCorporateCustomerSection(props: NonFsdsCorporateCustomerSectionProps) {
  const isLegacy = 'partners' in props && Array.isArray(props.partners) && props.partners.length > 0

  if (isLegacy) {
    return (
      <LegacyCardLayout
        title={props.title ?? ''}
        partners={props.partners ?? []}
        theme={props.theme}
      />
    )
  }

  return <MarqueeLayout {...props} />
}
